<script setup>
import { ref, reactive, computed, nextTick } from "vue";
import { Modal } from "bootstrap";
import { useInvoiceStore } from "@/stores/invoice";
import { useUnitStore } from "@/stores/unit";

const emit = defineEmits(["saved"]);

const invoiceStore = useInvoiceStore();
const unitStore = useUnitStore();
const modalEl = ref(null);
const isEditing = ref(false);

// ─── Computed ─────────────────────────────────────────
const occupiedUnits = computed(() =>
  unitStore.units.filter((u) => u.occupancyStatus === "occupied"),
);

// ─── Form & Errors ────────────────────────────────────
const today = new Date().toISOString().slice(0, 10);

const defaultForm = () => ({
  _id: null,
  unitCode: "",
  residentName: "",
  building: "",
  billingMonth: "",
  issueDate: today,
  dueDate: "",
  amount: 0,
  penalty: 0,
  note: "",
});

const form = reactive(defaultForm());
const errors = reactive({
  unitCode: "",
  billingMonth: "",
  issueDate: "",
  dueDate: "",
  amount: "",
});

// ─── Helpers ──────────────────────────────────────────
const getModal = () => Modal.getOrCreateInstance(modalEl.value);

const resetForm = () => {
  Object.assign(form, defaultForm());
  Object.keys(errors).forEach((k) => (errors[k] = ""));
};

// auto-fill ชื่อลูกบ้าน อาคาร และค่าส่วนกลาง เมื่อเลือกยูนิต
const onUnitChange = () => {
  const unit = unitStore.units.find((u) => u.unitCode === form.unitCode);
  form.residentName = unit?.residentName || "";
  form.building = unit?.building || "";
  form.amount = unit?.monthlyFee || 0;
};

const validate = () => {
  errors.unitCode = form.unitCode ? "" : "กรุณาเลือกยูนิต";
  errors.billingMonth = form.billingMonth ? "" : "กรุณาระบุรอบบิล";
  errors.issueDate = form.issueDate ? "" : "กรุณาระบุวันที่ออกบิล";
  errors.dueDate = form.dueDate ? "" : "กรุณาระบุวันครบกำหนด";
  errors.amount = Number(form.amount) > 0 ? "" : "กรุณาระบุค่าส่วนกลาง";
  return !Object.values(errors).some(Boolean);
};

// ─── Public API ───────────────────────────────────────
/**
 * open(invoice?)
 * - ไม่ส่ง invoice → โหมดสร้างใหม่
 * - ส่ง invoice   → โหมดแก้ไข พร้อมโหลดข้อมูลเดิม
 */
const open = (invoice = null) => {
  resetForm();
  if (invoice) {
    isEditing.value = true;
    Object.assign(form, {
      _id: invoice.id,
      unitCode: invoice.unitCode,
      residentName: invoice.residentName,
      building: invoice.building,
      billingMonth: invoice.billingMonth,
      issueDate: invoice.issueDate,
      dueDate: invoice.dueDate,
      amount: invoice.amount,
      penalty: invoice.penalty || 0,
      note: invoice.note || "",
    });
  } else {
    isEditing.value = false;
  }
  nextTick(() => getModal().show());
};

const close = () => getModal().hide();

// ─── Submit ───────────────────────────────────────────
const handleSubmit = () => {
  if (!validate()) return;

  if (isEditing.value) {
    invoiceStore.updateInvoice({ ...form, id: form._id });
  } else {
    invoiceStore.addInvoice({ ...form });
  }

  close();
  emit("saved", { mode: isEditing.value ? "edit" : "add" });
};

defineExpose({ open, close });
</script>
<template>
  <div class="modal fade" tabindex="-1" ref="modalEl">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content rounded-4 border-0 shadow">
        <div class="modal-header border-0 pb-0 px-4 pt-4">
          <h5 class="modal-title fw-bold">
            <i class="bi bi-receipt-cutoff me-1 text-primary"></i
            >{{ isEditing ? "แก้ไขใบแจ้งหนี้" : "ออกใบแจ้งหนี้ใหม่" }}
          </h5>
          <button type="button" class="btn-close" @click="close"></button>
        </div>

        <div class="modal-body px-4 py-3">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label fw-semibold">
                ยูนิต <span class="text-danger">*</span>
              </label>
              <select
                v-model="form.unitCode"
                class="form-select rounded-3"
                :class="{ 'is-invalid': errors.unitCode }"
                :disabled="isEditing"
                @change="onUnitChange"
              >
                <option value="">-- เลือกยูนิต --</option>
                <option
                  v-for="u in occupiedUnits"
                  :key="u.id"
                  :value="u.unitCode"
                >
                  {{ u.unitCode }} — {{ u.residentName }}
                </option>
              </select>
              <div class="invalid-feedback">{{ errors.unitCode }}</div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">ชื่อลูกบ้าน</label>
              <input
                :value="form.residentName"
                type="text"
                class="form-control rounded-3 bg-light"
                readonly
              />
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">
                รอบบิล <span class="text-danger">*</span>
              </label>
              <input
                v-model="form.billingMonth"
                type="text"
                class="form-control rounded-3"
                placeholder="เช่น เมษายน 2026"
                :class="{ 'is-invalid': errors.billingMonth }"
              />
              <div class="invalid-feedback">{{ errors.billingMonth }}</div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">อาคาร</label>
              <input
                :value="form.building"
                type="text"
                class="form-control rounded-3 bg-light"
                readonly
              />
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">
                วันที่ออกบิล <span class="text-danger">*</span>
              </label>
              <input
                v-model="form.issueDate"
                type="date"
                class="form-control rounded-3"
                :class="{ 'is-invalid': errors.issueDate }"
              />
              <div class="invalid-feedback">{{ errors.issueDate }}</div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">
                วันครบกำหนดชำระ <span class="text-danger">*</span>
              </label>
              <input
                v-model="form.dueDate"
                type="date"
                class="form-control rounded-3"
                :class="{ 'is-invalid': errors.dueDate }"
              />
              <div class="invalid-feedback">{{ errors.dueDate }}</div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">
                ค่าส่วนกลาง (บาท) <span class="text-danger">*</span>
              </label>
              <input
                v-model.number="form.amount"
                type="number"
                min="0"
                class="form-control rounded-3"
                :class="{ 'is-invalid': errors.amount }"
              />
              <div class="invalid-feedback">{{ errors.amount }}</div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">ค่าปรับ (บาท)</label>
              <input
                v-model.number="form.penalty"
                type="number"
                min="0"
                class="form-control rounded-3"
              />
            </div>

            <div class="col-12">
              <label class="form-label fw-semibold">หมายเหตุ</label>
              <textarea
                v-model="form.note"
                class="form-control rounded-3"
                rows="2"
                placeholder="บันทึกเพิ่มเติม"
              ></textarea>
            </div>
          </div>
        </div>

        <div class="modal-footer border-0 px-4 pb-4 pt-2 gap-2">
          <button
            type="button"
            class="btn btn-danger border rounded-3 px-4"
            @click="close"
          >
            <i class="bi bi-x-lg me-1"></i>ยกเลิก
          </button>
          <button
            type="button"
            class="btn rounded-3 px-4"
            :class="isEditing ? 'btn-warning' : 'btn-success'"
            @click="handleSubmit"
          >
            <i class="bi bi-floppy2 me-1"></i
            >{{ isEditing ? "บันทึกการแก้ไข" : "ออกใบแจ้งหนี้" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
